<template>
  <div>
    <navigation-top/>

    <div class="pt-8">
      <div class="header">
        <h2 class="pageTitle">Try Qlerify for free</h2>
        <p class="pageDescription">Discover the potential of AI-Powered Software Modeling and Code Creation</p>
      </div>

      <div class="d-flex align-center justify-center">
        <div class="switcher">
          <v-btn-toggle
            v-model="localSubscriptionType"
            color="var(--dark-4, #363A40)"
            group
            density="compact"
          >
            <v-btn
              value="monthly"
              class="pl-6 pr-6"
              :class="localSubscriptionType === 'monthly' ? 'text-white' : ''"
            >
              Monthly
            </v-btn>
            <v-btn
              value="yearly"
              class="pl-6 pr-6"
              :class="localSubscriptionType === 'yearly' ? 'text-white' : ''"
            >
              Yearly
            </v-btn>
          </v-btn-toggle>
        </div>
      </div>

      <div class="plans">
        <plan-card
          :key="plan.value"
          v-for="plan in PLANS"
          :name="plan.name"
          :description="plan.description"
          :monthlyPrice="plan.monthlyPrice"
          :yearlyPrice="plan.yearlyPrice"
          :type="localSubscriptionType"
          :popular="plan.value === 'pro'"
          @onSelect="openCheckout(plan.value)"
        />
        <plan-card
          v-if="ENTERPRISE_PLAN"
          :name="ENTERPRISE_PLAN.name"
          :description="ENTERPRISE_PLAN.description"
          @onSelect="openContactUs"
        />
      </div>

      <div class="compareInfo">
        <p class="compareInfoTitle">See how Qlerify compares to other tools</p>

        <table class="compareInfoTable">
          <thead>
            <tr>
              <th class="compareInfoTableHeader"></th>
              <th class="compareInfoTableHeader">
                <img
                  class="logo"
                  alt="Qlerify logo"
                  src="@/assets/logo-black.svg"
                  height="30"
                />
              </th>
              <th class="compareInfoTableHeader">Miro</th>
              <th class="compareInfoTableHeader">LucidChart</th>
              <th class="compareInfoTableHeader">Visio</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="info in compareInfo" :key="info.feature">
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  {{ info.feature }}
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.qlerify">
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.miro">
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.lucidChart">
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
              <td class="compareInfoTableItem">
                <div class="compareInfoTableItemInner">
                  <template v-if="info.visio">
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-checked.svg"
                    />
                  </template>
                  <template v-else>
                    <img
                      class="logo"
                      alt="Qlerify logo"
                      src="@/assets/compare-unchecked.svg"
                    />
                  </template>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="faq">
        <div class="faqInner">
          <v-expansion-panels>
            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I try Qlerify for free?"
              text="Yes, we offer a completely free 4-week trial of the Pro version of Qlerify with all its features. Just sign up, and if you decide not to continue, cancel before the trial ends to avoid charges."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Who is Qlerify for?"
              text="Anyone involved in transforming business needs to a business application, such as Product Owners, Product Managers, Architects, Business Analysts, CTOs, Founders, Team Leaders, Project Managers, Scrum Masters, etc."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is my data secure?"
            >
              <template v-slot:text>
                We take security seriously and are committed to protecting your data. We comply with GDPR and follow
                strict security measures. <a href="https://www.qlerify.com/security" target="_blank">Learn more</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What payment options do you accept?"
              text="We accept Visa, Mastercard, and other major credit cards. We also accept bank transfers and invoices."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens after my free trial ends?"
              text="If you cancel your plan before the 4-week trial ends, you will lose access to your projects and workflows. If you don’t cancel your subscription before the 4-week trial ends, your credit card will be charged automatically. However, you can export all your content to Jira, Azure DevOps or as JSON, CSV or PDF files before the trial ends."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens to my data if I cancel?"
              text="Your data will be stored for 6 months, allowing you to restore it if you decide to re-subscribe later."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I extend the trial period?"
            >
              <template v-slot:text>
                Yes, send an email to <a href="mailto:support@qlerify.com">support@qlerify.com</a> and we will extend
                your trial for 2 more weeks.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Do you offer discounts for annual plans?"
              text="Yes, we offer a 17% discount on annual plans."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I switch plans later?"
              text="Yes, you can switch from both Basic to Pro as well as from Pro to Basic."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Can I switch from monthly to yearly payment?"
              text="Yes, by going to “My Plan” in the Qlerify app you can update to annual billing."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is it possible to add or remove users later in the plan?"
              text="Yes, you can add or remove user seats in both the Qlerify Pro and Qlerify Basic plans. When removing a seat, you can either lower the cost by removing the seat or assign it to a new user."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="What happens if I cancel my subscription?"
              text="Your subscription will end automatically on the last day of your current subscription period."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is there a limit to the number of AI prompts?"
              text="No, with both the Qlerify Pro and Basic plan, you have unlimited AI prompts."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Does Qlerify integrate with other products?"
              text="Yes, Qlerify allows you to export cards to Jira and Azure DevOps. You can also export data as a CSV file, which can be opened in Google Sheets or Excel, or in JSON format. Custom integrations available in the Enterprise plan."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Is there a discount for startups, students, etc?"
            >
              <template v-slot:text>
                We offer discounts on a case-by-case basis. Please <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">contact
                  us</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Are there any applicable taxes?"
            >
              <template v-slot:text>
                All prices are excluding Swedish VAT. Since Qlerify is a Swedish company, we must collect VAT from some
                of our customers. Please <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">contact
                  us</a> for further information.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Where can I find my invoices?"
              text="You can find your invoices under “Plan & Billing” > “Billing” in the top right corner, under the profile icon."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Do you offer onboarding or training?"
            >
              <template v-slot:text>
                Yes, we offer onboarding and training. Please <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">contact
                  us</a> to schedule a training session
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="Do I need to install any software to use Qlerify?"
              text="No, Qlerify is entirely web based and can be used with Chrome or Edge. The Qlerify app works on all computers and operating systems."
            >
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How can I suggest a new feature?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">here</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How can I report a bug?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">here</a>.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How do I contact support?"
            >
              <template v-slot:text>
                Please fill out our support form <a
                  target="_blank"
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ">here</a>
                or
                <a href="tel:+46 (0)8 12420810">call us</a> at +46 (0)8 12420810.
              </template>
            </v-expansion-panel>

            <v-expansion-panel
              collapse-icon="mdi-minus" expand-icon="mdi-plus"
              title="How do I cancel my subscription?"
              text="It’s very easy to cancel your Qlerify subscription. You can find it under “Plan & Billing” > “My Plan” in the top right corner, under the profile icon."
            >
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import PlanCard from '@/components/PlanCard.vue'
import NavigationTop from '../components/NavigationTop.vue'
import { mapState } from 'vuex'
import { PLANS, ENTERPRISE_PLAN } from '@/utils/payments'

export default {
  components: {
    PlanCard,
    NavigationTop
  },
  computed: {
    ...mapState({
      subscriptionType: state => state.payments.subscriptionType,
      activeSubscription: state => state.payments.activeSubscription
    }),
    PLANS () {
      return PLANS || []
    },
    ENTERPRISE_PLAN () {
      return ENTERPRISE_PLAN || null
    }
  },
  data () {
    return {
      localSubscriptionType: 'monthly',
      compareInfo: [
        {
          feature: 'Automatic alignment and connection of diagram symbols',
          qlerify: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Support for Domain Driven Design', qlerify: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Support for Event Storming', qlerify: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Support for User Story Mapping', qlerify: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Support for Agile Data Modeling', qlerify: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Al generation of workflows, data models, API models, etc',
          qlerify: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Code generation of APls/server stubs', qlerify: true, miro: false, lucidChart: false, visio: false },
        { feature: 'Code generation of unit tests', qlerify: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Generation of Swagger (OAS) documentation',
          qlerify: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        {
          feature: 'Export of actionable software models (CSV, PDF, JSON, ...)',
          qlerify: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Jira Cloud Integration', qlerify: true, miro: true, lucidChart: true, visio: false },
        { feature: 'Scrum Product Backlog', qlerify: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Finetuning of customer specific Al model',
          qlerify: true,
          miro: false,
          lucidChart: false,
          visio: false
        },
        { feature: 'Support for Event Modeling', qlerify: true, miro: false, lucidChart: false, visio: false },
        {
          feature: 'Azure DevOps integration',
          qlerify: true,
          miro: true,
          lucidChart: false,
          visio: false
        }
      ]
    }
  },
  methods: {
    openCheckout (planValue) {
      const plan = PLANS.find(p => p.value === planValue)
      store.commit('payments/setPlan', plan)
      this.$router.push('checkout')
    },
    openContactUs () {
      window.open('https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ')
    }
  },
  mounted () {
    if (this.subscriptionType) {
      this.localSubscriptionType = this.subscriptionType
    }
    if (['active', 'trialing'].includes(this.activeSubscription?.subscriptionDetails?.status)) {
      this.$router.push('/subscription/my-plan')
    }
  },
  watch: {
    localSubscriptionType: function (subscriptionType) {
      store.commit('payments/setSubscriptionType', subscriptionType)
    },
    activeSubscription: function (activeSubscription) {
      if (['active', 'trialing'].includes(activeSubscription?.subscriptionDetails?.status)) {
        this.$router.push('/subscription/my-plan')
      }
    }
  }
}
</script>

<style scoped>
.header {
  margin-bottom: 56px;
}

.pageTitle {
  text-align: center;
  color: #000000;
  margin-bottom: 16px;
  font-family: Faktum, sans-serif;
  font-size: 52px;
  font-weight: 400;
  line-height: 62.4px;
  letter-spacing: -0.05em;
}

.pageDescription {
  font-size: 18px;
  font-weight: 400;
  line-height: 28.8px;
  text-align: center;
  letter-spacing: -0.02em;
  color: rgba(0, 0, 0, 0.80);
  max-width: 421px;
  margin: 0 auto;
}

.switcher {
  padding: 3px;
  border-radius: 8px;
  border: 1px solid var(--light-4);
  margin-bottom: 56px;
}

.plans {
  gap: 30px;
  display: flex;
  margin-bottom: 64px;
  align-items: flex-end;
  justify-content: center;
}

.compareInfo {
  width: 100%;
  margin: 0 auto;
  max-width: 1512px;
}

.compareInfoTitle {
  font-size: 36px;
  font-weight: 500;
  line-height: 42px;
  text-align: center;
  color: var(--dark-4);
  margin-bottom: 38px;
}

.compareInfoTable {
  width: 100%;
  margin: 0 auto 64px;
  border-collapse: collapse;

  tr {
    &:last-child {
      .compareInfoTableItem {
        border-bottom: none;
      }
    }
  }
}

.compareInfoTableHeader {
  font-weight: 400;
  font-size: 25px;
  padding: 14px 0;
  line-height: 32.5px;
  font-family: Faktum, sans-serif;
  border-bottom: 1px solid var(--light-4);
}

.compareInfoTableItem {
  font-size: 16px;
  color: var(--dark-4);
  line-height: 24px;
  padding: 8px 16px;
  border-bottom: 1px solid var(--light-4);

  .compareInfoTableItemInner {
    min-width: 100px;
    display: flex;
    justify-content: center;
  }

  &:first-child {
    max-width: 300px;

    .compareInfoTableItemInner {
      justify-content: flex-start;
    }
  }
}

.faq {
  gap: 16px;
  padding: 48px;
  background-color: var(--light-1);
}

.faqInner {
  max-width: 772px;
  margin: 0 auto;

  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :not(:first-child):not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--before-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :not(:first-child):not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--after-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :first-child:not(:last-child):not(.v-expansion-panel--active):not(.v-expansion-panel--before-active)),
  :global(.v-expansion-panels:not(.v-expansion-panels--variant-accordion) > :last-child:not(:first-child):not(.v-expansion-panel--active):not(.v-expansion-panel--after-active)) {
    border-radius: 8px !important;
  }

  :global(.v-expansion-panel-title) {
    font-size: 20px;
    font-weight: 500;
    font-family: Faktum, sans-serif;
    color: var(--text-dark, #1F2833);
  }

  :global(.v-expansion-panel-text__wrapper) {
    font-size: 16px;
    font-weight: 400;
    line-height: 25.6px;
    color: var(--text-dark, #1F2833);
  }

  :global(.v-expansion-panel::after) {
    display: none;
  }

  :global(.v-expansion-panel) {
    border-radius: 8px;
    margin-bottom: 8px;
    border: 1px solid var(--light-6);
  }

  :global(.v-expansion-panel__shadow) {
    box-shadow: none;
  }
}
</style>
